import { Button, Container, Grid, GridColumn, Heading, IFormHelperProps, List, ListItem, Notice, Separator, Text, TextField } from 'telia-front-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FORM_ERROR_CODE, FormErrors } from '../stores/EmailRegisterStore';
import RegistrationSuccessNotice from './RegistrationSuccessNotice';
import GaDataStore from '../stores/GaDataStore';
import { EMAIL_FIELD_ID, emailFieldHelper, getHelperErrorMessage, helperInErrorState, notEmptyFieldHelper } from '../util/FieldHelperUtil';
import { isEmailValid } from '../util/ValidationUtil';
import useStores from '../stores/UseStores';

const FIRSTNAME_FIELD_ID = 'first-name';
const LASTNAME_FIELD_ID = 'last-name';

const EmailRegister = observer((): React.JSX.Element => {
  const {
    emailRegisterStore,
    translateStore: { translate },
  } = useStores();
  const { emailRegisterCredentials, formStatus, submitting, showSuccess, getError: errorKey } = emailRegisterStore;
  const [emailDirty, setEmailDirty] = React.useState<boolean>(false);
  const [firstNameDirty, setFirstNameDirty] = React.useState<boolean>(false);
  const [lastNameDirty, setLastNameDirty] = React.useState<boolean>(false);
  const [isEmailInvalid, setIsEmailInvalid] = React.useState<boolean>(false);
  const [isFirstNameInvalid, setIsFirstNameInvalid] = React.useState<boolean>(false);
  const [isLastNameInvalid, setIsLastNameInvalid] = React.useState<boolean>(false);

  const [emailHelper, setEmailHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [firstNameHelper, setFirstNameHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [lastNameHelper, setLastNameHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  React.useEffect(() => {
    const helper = emailFieldHelper(translate, emailRegisterCredentials.email, emailDirty);

    setEmailHelper(helper);
    setIsEmailInvalid(helperInErrorState(helper));
  }, [emailRegisterCredentials.email, emailDirty]);

  React.useEffect(() => {
    const helper = notEmptyFieldHelper(translate, emailRegisterCredentials.firstName, firstNameDirty);

    setFirstNameHelper(helper);
    setIsFirstNameInvalid(helperInErrorState(helper));
  }, [emailRegisterCredentials.firstName, firstNameDirty]);

  React.useEffect(() => {
    const helper = notEmptyFieldHelper(translate, emailRegisterCredentials.lastName, lastNameDirty);

    setLastNameHelper(helper);
    setIsLastNameInvalid(helperInErrorState(helper));
  }, [emailRegisterCredentials.lastName, lastNameDirty]);

  React.useEffect(() => {
    return () => {
      emailRegisterStore.setEmptyFormStatus();
    };
  }, []);

  const isInvalid = () => {
    return (
      !emailRegisterCredentials.email ||
      !emailRegisterCredentials.firstName ||
      !emailRegisterCredentials.lastName ||
      !isEmailValid(emailRegisterCredentials.email)
    );
  };

  const handleFormSubmit = (event: React.FormEvent): void => {
    event.preventDefault();

    setEmailDirty(true);
    setFirstNameDirty(true);
    setLastNameDirty(true);

    if (!isInvalid()) {
      emailRegisterStore.handleEmailRegisterSubmit();
    } else {
      emailRegisterStore.setErrorState('generalError', FORM_ERROR_CODE);
    }
  };

  const renderErrorNotice = () => {
    const getErroredFieldButton = (fieldId: string) => (
      <ListItem key={`erroredField-${fieldId}-button`}>
        <Button text={translate(`errored.field.button.${fieldId}`)} type="withdrawal" link onClick={() => document.getElementById(fieldId)?.focus()} />
      </ListItem>
    );
    const checkLabelContainsField = (label: string | undefined, field: string) => label?.toLowerCase().includes(field);

    if (!errorKey) {
      return null;
    }
    if (errorKey) {
      const label: string | undefined = formStatus[errorKey as keyof FormErrors] || undefined;
      label && GaDataStore.error(translate(label));
      const isEmailError = errorKey === 'emailError';
      if (isEmailError || label) {
        const isEmailErrored = isEmailInvalid || checkLabelContainsField(label, 'email');
        const isFirstNameErrored = isEmailInvalid || checkLabelContainsField(label, 'firstname');
        const isLastNameErrored = isEmailInvalid || checkLabelContainsField(label, 'lastname');

        return (
          <GridColumn width={['xs-10']}>
            <Text>
              <Notice type="error" attributes={{ 'aria-live': 'polite', 'aria-atomic': 'true' }}>
                <div>
                  {translate(isEmailError ? FORM_ERROR_CODE : label || '')}
                  {[isEmailErrored, isFirstNameErrored, isLastNameErrored].some((fieldInvalid) => fieldInvalid === true) && (
                    <>
                      <Separator spacing="xxsmall" color="invisible" />
                      <List type="unstyled">
                        {isEmailErrored && getErroredFieldButton(EMAIL_FIELD_ID)}
                        {isFirstNameErrored && getErroredFieldButton(FIRSTNAME_FIELD_ID)}
                        {isLastNameErrored && getErroredFieldButton(LASTNAME_FIELD_ID)}
                      </List>
                    </>
                  )}
                </div>
              </Notice>
              <Separator spacing="xsmall" color="invisible" />
            </Text>
          </GridColumn>
        );
      }
    }
  };

  if (showSuccess) {
    return <RegistrationSuccessNotice />;
  }

  return (
    <Container>
      <Grid align={['center-xs']}>
        <GridColumn width={['xs-12', 'sm-10', 'md-8', 'lg-6']}>
          <Grid align={['center-xs']} className="text-center">
            <GridColumn width={['xs-8', 'sm-6']}>
              <Text className="text-center">
                <Heading level={1} className="h3">
                  {translate('email_register.header')}
                </Heading>
                <Separator color="invisible" spacing="xsmall" />
              </Text>
            </GridColumn>
            {renderErrorNotice()}
            <GridColumn width={['xs-10', 'sm-8']}>
              <form onSubmit={handleFormSubmit} id="emailLogin">
                <Text>
                  <p>{translate('email_register.description')}</p>
                  <TextField
                    label={translate('panel.email.input')}
                    id={EMAIL_FIELD_ID}
                    value={emailRegisterCredentials.email}
                    onChange={(value) => (emailRegisterCredentials.email = value)}
                    isInvalid={isEmailInvalid || !!formStatus?.emailError}
                    errorMessage={getHelperErrorMessage(emailHelper) || (!!formStatus?.emailError ? translate(formStatus.emailError) : undefined)}
                    required={true}
                    input={{ autoComplete: 'email' }}
                  />
                  <TextField
                    label={translate('panel.firstName.input')}
                    id={FIRSTNAME_FIELD_ID}
                    value={emailRegisterCredentials.firstName}
                    onChange={(value) => (emailRegisterCredentials.firstName = value)}
                    isInvalid={isFirstNameInvalid || !!formStatus?.firstNameError}
                    errorMessage={
                      getHelperErrorMessage(firstNameHelper) || (!!formStatus?.firstNameError ? translate('error.mandatory_field_is_empty') : undefined)
                    }
                    required={true}
                    input={{ autoComplete: 'given-name' }}
                  />
                  <TextField
                    label={translate('panel.lastName.input')}
                    id={LASTNAME_FIELD_ID}
                    value={emailRegisterCredentials.lastName}
                    onChange={(value) => (emailRegisterCredentials.lastName = value)}
                    isInvalid={isLastNameInvalid || !!formStatus?.lastNameError}
                    errorMessage={
                      getHelperErrorMessage(lastNameHelper) || (!!formStatus?.lastNameError ? translate('error.mandatory_field_is_empty') : undefined)
                    }
                    required={true}
                    input={{ autoComplete: 'family-name' }}
                  />
                  <Button
                    text={translate('email_register.register_button_label')}
                    iconRight="#arrow-right"
                    onClick={handleFormSubmit}
                    disabled={submitting}
                    isLoading={submitting}
                  />
                </Text>
              </form>
            </GridColumn>
          </Grid>
        </GridColumn>
      </Grid>
    </Container>
  );
});

export default EmailRegister;
